import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'EN' | 'ZU' | 'XH' | 'ZH';

interface LanguageContextType {
  currentLanguage: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation data
const translations: Record<Language, Record<string, string>> = {
  EN: {
    // Header
    'header.search.placeholder': 'Search restaurants, groceries, medication...',
    'header.location': 'Durban',
    'header.orderNow': 'Order Now',
    
    // Hero Section
    'hero.title': 'Late night cravings? Sorted.',
    'hero.subtitle': 'Restaurant food, groceries, essentials, medication, beverages & plants delivered',
    'hero.cta.primary': 'Order Now',
    'hero.cta.secondary': 'Browse Stores',
    
    // Categories
    'categories.title': 'What do you need?',
    'categories.all': 'All',
    'categories.restaurants': 'Restaurants',
    'categories.groceries': 'Groceries',
    'categories.medication': 'Medication',
    'categories.beverages': 'Beverages',
    'categories.essentials': 'Essentials',
    'categories.plants': 'Plants',
    
    // Stores
    'stores.title': 'Stores',
    'stores.subtitle': 'Discover late-night favorites in your area',
    'stores.loadMore': 'Load More Stores',
    
    // Restaurants
    'restaurants.title': 'Popular Restaurants',
    'restaurants.subtitle': 'Discover the most loved spots in your area',
    'restaurants.showing': 'Showing {count} restaurants',
    'restaurants.loadMore': 'Load More Restaurants',
    'restaurants.open': 'Open',
    'restaurants.closed': 'Closed',
    'restaurants.availableNow': 'Available now',
    'restaurants.minOrder': 'Min order: R80',
    
    // Order Status
    'order.confirmed': 'Order Confirmed',
    'order.preparing': 'Preparing Food',
    'order.outForDelivery': 'Out for Delivery',
    'order.delivered': 'Delivered',
    
    // Common
    'common.back': 'Back',
    'common.total': 'Total',
    'common.checkout': 'Proceed to Checkout',
    'common.placeOrder': 'Place Order',
    'common.loading': 'Loading...',
    'common.processing': 'Processing...',
    
    // Footer
    'footer.tagline': 'Your trusted after-hours marketplace. Delivering restaurant food, groceries, medication, beverages & plants 11 PM - 7 AM.',
    'footer.quickLinks': 'Quick Links',
    'footer.browseRestaurants': 'Browse Marketplace',
    'footer.becomePartner': 'Become a Partner',
    'footer.driverSignUp': 'Driver Sign Up',
    'footer.helpCenter': 'Help Center',
    'footer.languages': 'Languages',
    'footer.contact': 'Contact',
    'footer.copyright': '© 2024 LUMZA After-Hours Delivery. All rights reserved.',
    
    // Vendor Dashboard
    'vendor.dashboard.title': 'Vendor Dashboard',
    'vendor.dashboard.welcome': 'Welcome back, {name}!',
    'vendor.dashboard.todayOrders': "Today's Orders",
    'vendor.dashboard.todayRevenue': "Today's Revenue",
    'vendor.dashboard.weeklyOrders': 'Weekly Orders',
    'vendor.dashboard.rating': 'Rating',
    'vendor.dashboard.activeOrders': 'Active Orders',
    'vendor.dashboard.quickActions': 'Quick Actions',
    'vendor.dashboard.manageMenu': 'Manage Menu',
    'vendor.dashboard.viewAnalytics': 'View Analytics',
    'vendor.dashboard.settings': 'Restaurant Settings',
    
    // Driver Dashboard
    'driver.dashboard.title': 'Driver Dashboard',
    'driver.dashboard.welcome': 'Welcome back, {name}!',
    'driver.dashboard.todayEarnings': "Today's Earnings",
    'driver.dashboard.todayTrips': "Today's Trips",
    'driver.dashboard.completionRate': 'Completion Rate',
    'driver.dashboard.activeDelivery': 'Active Delivery',
    'driver.dashboard.pickup': 'Pickup',
    'driver.dashboard.dropoff': 'Dropoff',
    'driver.dashboard.earnings': 'Earnings',
    'driver.dashboard.navigateToPickup': 'Navigate to Pickup',
    'driver.dashboard.callCustomer': 'Call Customer'
  },
  
  ZU: {
    // Header
    'header.search.placeholder': 'Sesha ukudla, ama-groceries, imithi...',
    'header.location': 'eThekwini',
    'header.orderNow': 'Oda Manje',
    
    // Hero Section
    'hero.title': 'Ebusuku? Sikulungele, mfethu.',
    'hero.subtitle': 'Ukudla, izinto zokubraya, imithi nezinto ezidingekayo — emnyango wakho kusuka ngo',
    'hero.cta.primary': 'Oda Manje',
    'hero.cta.secondary': 'Bheka Izitolo',
    
    // Categories
    'categories.title': 'Yini oyidingayo?',
    'categories.all': 'Konke',
    'categories.restaurants': 'Izindawo Zokudla',
    'categories.groceries': 'Ama-Groceries',
    'categories.medication': 'Imithi',
    'categories.beverages': 'Iziphuzo',
    'categories.essentials': 'Izidingo',
    'categories.plants': 'Izitshalo',
    
    // Stores
    'stores.title': 'Izitolo Nezindawo Zokudla Eziseduze',
    'stores.subtitle': 'Thola izinto ozithandayo ebusuku',
    'stores.loadMore': 'Layisha Ezinye Izitolo',
    
    // Restaurants
    'restaurants.title': 'Izindawo Zokudla Ezithandwayo',
    'restaurants.subtitle': 'Thola izindawo ezithandwa kakhulu endaweni yakho',
    'restaurants.showing': 'Kubonisa izindawo zokudla ezingu-{count}',
    'restaurants.loadMore': 'Layisha Ezinye Izindawo Zokudla',
    'restaurants.open': 'Kuvuliwe',
    'restaurants.closed': 'Kuvalwe',
    'restaurants.availableNow': 'Kuyatholakala manje',
    'restaurants.minOrder': 'Ukuoda okuncane: R80',
    
    // Order Status
    'order.confirmed': 'Ukuoda Kuqinisekisiwe',
    'order.preparing': 'Kulungiswa Ukudla',
    'order.outForDelivery': 'Kuyalethwa',
    'order.delivered': 'Kulethiwe',
    
    // Common
    'common.back': 'Buyela',
    'common.total': 'Isamba',
    'common.checkout': 'Qhubeka Nokukhokha',
    'common.placeOrder': 'Beka Ukuoda',
    'common.loading': 'Kulayishwa...',
    'common.processing': 'Kuyacutshungulwa...',
    
    // Footer
    'footer.tagline': 'Imakethe yakho ebusuku. Siletha ukudla, ama-groceries, imithi, iziphuzo nezitshalo 11 PM - 7 AM.',
    'footer.quickLinks': 'Izixhumanisi Ezisheshayo',
    'footer.browseRestaurants': 'Bheka Imakethe',
    'footer.becomePartner': 'Yiba Umlingani',
    'footer.driverSignUp': 'Ukubhalisela Umshayeli',
    'footer.helpCenter': 'Isikhungo Sosizo',
    'footer.languages': 'Izilimi',
    'footer.contact': 'Xhumana Nathi',
    'footer.copyright': '© 2024 LUMZA After-Hours Delivery. Onke amalungelo agodliwe.',
    
    // Vendor Dashboard
    'vendor.dashboard.title': 'Ideshibhodi Yomthengisi',
    'vendor.dashboard.welcome': 'Sawubona futhi, {name}!',
    'vendor.dashboard.todayOrders': 'Ama-oda Anamuhla',
    'vendor.dashboard.todayRevenue': 'Imali Yanamuhla',
    'vendor.dashboard.weeklyOrders': 'Ama-oda Evikini',
    'vendor.dashboard.rating': 'Izikalo',
    'vendor.dashboard.activeOrders': 'Ama-oda Asebenzayo',
    'vendor.dashboard.quickActions': 'Izenzo Ezisheshayo',
    'vendor.dashboard.manageMenu': 'Phatha Imenyu',
    'vendor.dashboard.viewAnalytics': 'Bona Uhlaziyo',
    'vendor.dashboard.settings': 'Izilungiselelo Zendawo Yokudla',
    
    // Driver Dashboard
    'driver.dashboard.title': 'Ideshibhodi Yomshayeli',
    'driver.dashboard.welcome': 'Sawubona futhi, {name}!',
    'driver.dashboard.todayEarnings': 'Okutholiwe Namuhla',
    'driver.dashboard.todayTrips': 'Uhambo Lwanamuhla',
    'driver.dashboard.completionRate': 'Izinga Lokuqedela',
    'driver.dashboard.activeDelivery': 'Ukulethwa Okusebenzayo',
    'driver.dashboard.pickup': 'Ukuthatha',
    'driver.dashboard.dropoff': 'Ukuhlisa',
    'driver.dashboard.earnings': 'Okutholiwe',
    'driver.dashboard.navigateToPickup': 'Zulazulela Ekuthatheni',
    'driver.dashboard.callCustomer': 'Shayela Ikhasimende'
  },
  
  XH: {
    // Header
    'header.search.placeholder': 'Khangela ukutya, igrocrhi, amayeza...',
    'header.location': 'eThekwini',
    'header.orderNow': 'Odola Ngoku',
    
    // Hero Section
    'hero.title': 'Ebusuku? Sikho, mhlobo wam.',
    'hero.subtitle': 'Ukutya, ibraai packs, amayeza nezinto ezifunekayo — emnyangweni wakho ukususela ngo',
    'hero.cta.primary': 'Odola Ngoku',
    'hero.cta.secondary': 'Jonga Iivenkile',
    
    // Categories
    'categories.title': 'Ufunani?',
    'categories.all': 'Konke',
    'categories.restaurants': 'Iindawo Zokutyela',
    'categories.groceries': 'Igrocrhi',
    'categories.medication': 'Amayeza',
    'categories.beverages': 'Iziselo',
    'categories.essentials': 'Izinto Ezibalulekileyo',
    'categories.plants': 'Izityalo',
    
    // Stores
    'stores.title': 'Iivenkile Neendawo Zokutyela Ezikufutshane',
    'stores.subtitle': 'Fumana izinto ozithandayo ebusuku',
    'stores.loadMore': 'Layisha Ezinye Iivenkile',
    
    // Restaurants
    'restaurants.title': 'Iindawo Zokutyela Ezithandwayo',
    'restaurants.subtitle': 'Fumana iindawo ezithandwa kakhulu kwindawo yakho',
    'restaurants.showing': 'Kuboniswa iindawo zokutyela ezingu-{count}',
    'restaurants.loadMore': 'Layisha Ezinye Iindawo Zokutyela',
    'restaurants.open': 'Kuvuliwe',
    'restaurants.closed': 'Kuvalwe',
    'restaurants.availableNow': 'Kuyafumaneka ngoku',
    'restaurants.minOrder': 'Ukuodola okuncinci: R80',
    
    // Order Status
    'order.confirmed': 'Ukuodola Kuqinisekisiwe',
    'order.preparing': 'Kulungiswa Ukutya',
    'order.outForDelivery': 'Kuyaziswa',
    'order.delivered': 'Kuzisiwe',
    
    // Common
    'common.back': 'Buyela',
    'common.total': 'Iyonke',
    'common.checkout': 'Hlawula',
    'common.placeOrder': 'Beka Ukuodola',
    'common.loading': 'Kulayishwa...',
    'common.processing': 'Kuyacutshungulwa...',
    
    // Footer
    'footer.tagline': 'Imarike yakho yasebusuku. Sizisa ukutya, igrocrhi, amayeza, iziselo nezityalo 11 PM - 7 AM.',
    'footer.quickLinks': 'Iindlela Ezikhawulezayo',
    'footer.browseRestaurants': 'Jonga Imarike',
    'footer.becomePartner': 'Yiba Liqabane',
    'footer.driverSignUp': 'Ukubhalisa Umqhubi',
    'footer.helpCenter': 'Iziko Loncedo',
    'footer.languages': 'Iilwimi',
    'footer.contact': 'Qhagamshelana Nathi',
    'footer.copyright': '© 2024 LUMZA After-Hours Delivery. Onke amalungelo agodliwe.',
    
    // Vendor Dashboard
    'vendor.dashboard.title': 'Ideshibhodi Yomthengisi',
    'vendor.dashboard.welcome': 'Wamkelekile kwakhona, {name}!',
    'vendor.dashboard.todayOrders': 'Iiodolo Zanamhlanje',
    'vendor.dashboard.todayRevenue': 'Ingeniso Yanamhlanje',
    'vendor.dashboard.weeklyOrders': 'Iiodolo Zeveki',
    'vendor.dashboard.rating': 'Ukuhlolwa',
    'vendor.dashboard.activeOrders': 'Iiodolo Ezisebenzayo',
    'vendor.dashboard.quickActions': 'Izenzo Ezikhawulezayo',
    'vendor.dashboard.manageMenu': 'Lawula Imenyu',
    'vendor.dashboard.viewAnalytics': 'Jonga Uhlahlelo',
    'vendor.dashboard.settings': 'Iisetingi Zendawo Yokutyela',
    
    // Driver Dashboard
    'driver.dashboard.title': 'Ideshibhodi Yomqhubi',
    'driver.dashboard.welcome': 'Wamkelekile kwakhona, {name}!',
    'driver.dashboard.todayEarnings': 'Okufunyenwe Namhlanje',
    'driver.dashboard.todayTrips': 'Uhambo Lwanamhlanje',
    'driver.dashboard.completionRate': 'Izinga Lokugqitywa',
    'driver.dashboard.activeDelivery': 'Ukuziswa Okusebenzayo',
    'driver.dashboard.pickup': 'Ukuthatha',
    'driver.dashboard.dropoff': 'Ukuhlisa',
    'driver.dashboard.earnings': 'Okufunyenwe',
    'driver.dashboard.navigateToPickup': 'Khokela Ekuthatheni',
    'driver.dashboard.callCustomer': 'Fona Umthengi'
  },
  
  ZH: {
    // Header
    'header.search.placeholder': '搜索餐厅、杂货、药品...',
    'header.location': '德班',
    'header.orderNow': '立即订购',
    
    // Hero Section
    'hero.title': '深夜肚子饿？我们来搞定。',
    'hero.subtitle': '美食、烧烤套餐、药品和日用品 — 每晚送到家门口，从',
    'hero.cta.primary': '立即下单',
    'hero.cta.secondary': '浏览商店',
    
    // Categories
    'categories.title': '您需要什么？',
    'categories.all': '全部',
    'categories.restaurants': '餐厅',
    'categories.groceries': '杂货',
    'categories.medication': '药品',
    'categories.beverages': '饮料',
    'categories.essentials': '生活必需品',
    'categories.plants': '植物',
    
    // Stores
    'stores.title': '附近商店和餐厅',
    'stores.subtitle': '发现深夜最爱',
    'stores.loadMore': '加载更多商店',
    
    // Restaurants
    'restaurants.title': '热门餐厅',
    'restaurants.subtitle': '发现您附近最受欢迎的地方',
    'restaurants.showing': '显示 {count} 家餐厅',
    'restaurants.loadMore': '加载更多餐厅',
    'restaurants.open': '营业中',
    'restaurants.closed': '已打烊',
    'restaurants.availableNow': '现在可用',
    'restaurants.minOrder': '最低订单：R80',
    
    // Order Status
    'order.confirmed': '订单已确认',
    'order.preparing': '准备食物中',
    'order.outForDelivery': '配送中',
    'order.delivered': '已送达',
    
    // Common
    'common.back': '返回',
    'common.total': '总计',
    'common.checkout': '去结账',
    'common.placeOrder': '下订单',
    'common.loading': '加载中...',
    'common.processing': '处理中...',
    
    // Footer
    'footer.tagline': '您值得信赖的深夜市场。配送餐食、杂货、药品、饮料和植物 晚11点-早7点。',
    'footer.quickLinks': '快速链接',
    'footer.browseRestaurants': '浏览市场',
    'footer.becomePartner': '成为合作伙伴',
    'footer.driverSignUp': '司机注册',
    'footer.helpCenter': '帮助中心',
    'footer.languages': '语言',
    'footer.contact': '联系我们',
    'footer.copyright': '© 2024 LUMZA深夜配送。保留所有权利。',
    
    // Vendor Dashboard
    'vendor.dashboard.title': '商家控制台',
    'vendor.dashboard.welcome': '欢迎回来，{name}！',
    'vendor.dashboard.todayOrders': '今日订单',
    'vendor.dashboard.todayRevenue': '今日收入',
    'vendor.dashboard.weeklyOrders': '本周订单',
    'vendor.dashboard.rating': '评分',
    'vendor.dashboard.activeOrders': '活跃订单',
    'vendor.dashboard.quickActions': '快速操作',
    'vendor.dashboard.manageMenu': '管理菜单',
    'vendor.dashboard.viewAnalytics': '查看分析',
    'vendor.dashboard.settings': '餐厅设置',
    
    // Driver Dashboard
    'driver.dashboard.title': '司机控制台',
    'driver.dashboard.welcome': '欢迎回来，{name}！',
    'driver.dashboard.todayEarnings': '今日收入',
    'driver.dashboard.todayTrips': '今日行程',
    'driver.dashboard.completionRate': '完成率',
    'driver.dashboard.activeDelivery': '活跃配送',
    'driver.dashboard.pickup': '取货',
    'driver.dashboard.dropoff': '送达',
    'driver.dashboard.earnings': '收入',
    'driver.dashboard.navigateToPickup': '导航至取货点',
    'driver.dashboard.callCustomer': '致电客户'
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('lumza-language');
    return (saved as Language) || 'EN';
  });

  useEffect(() => {
    localStorage.setItem('lumza-language', currentLanguage);
  }, [currentLanguage]);

  const setLanguage = (language: Language) => {
    setCurrentLanguage(language);
  };

  const t = (key: string, replacements?: Record<string, string | number>): string => {
    let translation = translations[currentLanguage]?.[key] || translations['EN'][key] || key;
    
    // Handle string replacements like {name}, {count}
    if (replacements) {
      Object.entries(replacements).forEach(([placeholder, value]) => {
        translation = translation.replace(`{${placeholder}}`, String(value));
      });
    }
    
    return translation;
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};